import { useState } from "react";
import {
  useGenAgentStatusQuery,
  useCreateAgentTokenMutation,
  useAgentConcurrencyQuery,
  useSetAgentConcurrencyMutation,
} from "@/api/settings";
import { apiUrl } from "@/api/client";

const PROVIDER_LABELS: Record<string, string> = {
  claude: "Claude Code",
  codex: "Codex",
  opencode: "OpenCode",
};

/** The local agent: one-paste setup with a live connection dot.
 *  While the agent heartbeats, this user's renders AND generations run on
 *  their machine — renders at desktop speed, generations on whichever
 *  provider CLI (Claude Code / Codex / OpenCode) is logged in there, so a
 *  personal subscription can power generations without its credentials ever
 *  touching the server. Outputs upload back so the app behaves identically. */
export function LocalRenderCard() {
  const { data: status } = useGenAgentStatusQuery();
  const { data: concurrency } = useAgentConcurrencyQuery();
  const setConcurrency = useSetAgentConcurrencyMutation();
  const mintToken = useCreateAgentTokenMutation();
  const [command, setCommand] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const alive = status?.alive ?? false;
  const providers = status?.providers ?? [];

  const generate = async () => {
    const { token } = await mintToken.mutateAsync();
    const base = apiUrl("").replace(/\/api\/?$/, "");
    const isWindows = navigator.userAgent.includes("Windows");
    setCommand(
      isWindows
        ? `iwr ${base}/agent.ps1 -OutFile agent.ps1; powershell -ExecutionPolicy Bypass -File agent.ps1 -Token ${token}`
        : `curl -fsSL ${base}/agent.sh | bash -s -- --token ${token}`,
    );
    setCopied(false);
  };

  return (
    <div className="mt-6 rounded-xl border border-line bg-surface-2">
      <div className="flex items-center justify-between border-b border-line px-6 py-4">
        <h2 className="text-sm font-semibold text-ink">Local Agent</h2>
        <span className="flex items-center gap-1.5 text-xs text-ink-2">
          <span
            className={`inline-block h-2 w-2 rounded-full ${alive ? "bg-success" : "bg-ink-3"}`}
          />
          {alive ? "Connected — jobs run on your machine" : "Not connected"}
        </span>
      </div>
      <div className="space-y-3 p-6">
        <p className="text-xs text-ink-3">
          Run MotionForge jobs on your own computer. <strong>Renders</strong>{" "}
          finish at desktop speed and save to{" "}
          <code className="rounded bg-code px-1">~/MotionForge-Renders/</code>{" "}
          (they still upload here too). <strong>AI generations</strong> run on
          whichever provider CLI is signed in on that machine — Claude Code,
          Codex, or OpenCode — so your own subscription does the generating and
          its credentials never leave your computer. Close the terminal any
          time and everything falls back to the server.
        </p>
        {alive && (
          <p className="text-xs text-ink-2">
            Generation providers detected on your machine:{" "}
            {providers.length > 0 ? (
              providers.map((p) => (
                <span
                  key={p}
                  className="mr-1 rounded bg-code px-1.5 py-0.5 text-[11px] text-ink"
                >
                  {PROVIDER_LABELS[p] ?? p}
                </span>
              ))
            ) : (
              <span className="text-ink-3">
                none — install and sign in to Claude Code, Codex, or OpenCode
                there, then restart the agent
              </span>
            )}
          </p>
        )}
        <div className="flex items-center gap-2">
          <label htmlFor="agent-gen-concurrency" className="text-xs text-ink-2">
            Max concurrent generations on your machine:
          </label>
          <select
            id="agent-gen-concurrency"
            value={concurrency?.gen_concurrency ?? 1}
            disabled={concurrency === undefined || setConcurrency.isPending}
            onChange={(e) => setConcurrency.mutate(Number(e.target.value))}
            className="rounded-lg border border-line bg-surface px-2 py-1 text-xs text-ink disabled:opacity-40"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
          <span className="text-[11px] text-ink-3">
            {setConcurrency.isPending
              ? "Saving…"
              : "applies to a running agent within seconds. Each slot runs a full provider CLI against your subscription's rate limits — 2 is a sensible ceiling for most plans."}
          </span>
        </div>
        <div className="space-y-1 text-xs text-ink-3">
          <p className="font-medium text-ink-2">How to connect:</p>
          <p>
            1. On the computer that should do the work, sign in to the provider
            CLI you want generations to use (run{" "}
            <code className="rounded bg-code px-1">claude</code> once and log
            in — or <code className="rounded bg-code px-1">codex</code> /{" "}
            <code className="rounded bg-code px-1">opencode</code>). Skip this
            if you only want local renders.
          </p>
          <p>
            2. Click the button below and paste the command into a terminal on
            that machine. It installs what it needs, connects, and the dot
            above turns green within seconds. Keep the window open while you
            work.
          </p>
        </div>
        {command ? (
          <div className="flex items-center gap-2">
            <code className="flex-1 overflow-x-auto whitespace-nowrap rounded-lg border border-line bg-code px-3 py-2 text-[11px] text-ink">
              {command}
            </code>
            <button
              onClick={() => {
                void navigator.clipboard.writeText(command);
                setCopied(true);
              }}
              className="shrink-0 rounded-lg bg-accent px-3 py-2 text-xs font-medium text-accent-fg transition-colors hover:bg-accent-hover"
            >
              {copied ? "Copied!" : "Copy"}
            </button>
          </div>
        ) : (
          <button
            onClick={() => void generate()}
            disabled={mintToken.isPending}
            className="rounded-lg bg-accent px-4 py-2 text-sm font-medium text-accent-fg transition-colors hover:bg-accent-hover disabled:opacity-40"
          >
            {mintToken.isPending ? "Generating…" : "Get connection command"}
          </button>
        )}
        {command && (
          <p className="text-[11px] text-ink-3">
            The command contains a 90-day personal token — treat it like a
            password. The agent keeps itself connected from then on; re-click
            here any time for a fresh command. To choose where render files
            land, append{" "}
            <code className="rounded bg-code px-1">--out /path</code> (or{" "}
            <code className="rounded bg-code px-1">-Out C:\path</code> on
            Windows).
          </p>
        )}
      </div>
    </div>
  );
}
